import { z } from 'zod';

import { BrandMediaService } from '@inference.engine/guidelines-api-client/web';
import { guidelinesHttp } from '@/lib/api/guidelines-client';
import { describeApiFailure } from '@/shared/api/describeApiFailure';
import type { BrandMediaKind, ListBrandMediaQuery } from '@/shared/lib/brand-media';

/**
 * One thing the library's describer could see in focus, and where it sits.
 *
 * The same 0-1 top-left-plus-extent frame `engineMeta.focal` uses, so a consumer reading both
 * cannot read them differently.
 *
 * Parsed rather than asserted, unlike the rest of this file. These four numbers are the only part
 * of the row that is arithmetic and layout: the panel prints `x.toFixed(2)` and the overlay writes
 * each one into a CSS percentage. The column is jsonb with no database-side shape, and `enrich`
 * writes what it is handed, so a row off-shape by any route reaches the browser as a `TypeError`
 * inside render or as a box positioned at `NaN%`. A parse at the boundary turns both into a
 * picture that simply carries no boxes.
 */
const DetectedObjectSchema = z.object({
  name: z.string(),
  // Plain `z.number()` already refuses NaN and Infinity, which is the shape that would reach the
  // overlay as a box positioned at `NaN%`.
  x: z.number(),
  y: z.number(),
  w: z.number(),
  h: z.number(),
});

export type DetectedObject = z.infer<typeof DetectedObjectSchema>;

/**
 * The boxes a row can be drawn with, and nothing else.
 *
 * A malformed entry is dropped rather than failing the row: one bad box should cost its own
 * rectangle, not the asset's descriptions, tags and thumbnail. Absent and null both come back as
 * undefined, which is what the panel already reads as "nothing has looked at this yet".
 */
const detectedObjectsFrom = (value: unknown): DetectedObject[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }
  return value.flatMap((entry) => {
    const parsed = DetectedObjectSchema.safeParse(entry);
    return parsed.success ? [parsed.data] : [];
  });
};

/**
 * Hand mirrored rather than generated: `GET /brand-media` declares no response schema, so
 * `BrandMediaDto` never reaches the OpenAPI spec and the call below casts. Adding a field here means
 * checking it against `BrandMediaDto` in guidelines-api by eye.
 */
export interface BrandMediaItem {
  id: string;
  assetId: string;
  thumbnailAssetId: string | null;
  /**
   * A still drawn from the clip, null for images.
   *
   * Load bearing for footage: an `img` pointed at the clip's own asset renders nothing, so this is
   * the only thing a thumbnail can draw for a video.
   */
  posterAssetId: string | null;
  title: string;
  /** One library holds both, and only this says which a row is. */
  mediaType: 'image' | 'video';
  placementClass: 'background' | 'slot';
  /** What the media is - logo, photography, product. Distinct from `placementClass`. */
  kind: BrandMediaKind;
  /**
   * What the file is.
   *
   * Load bearing rather than metadata: a slot an atom sources renders through `pack.assets`, which
   * substitution fills with SVG markup only. A raster lands in `pack.images` instead and is
   * unreachable from an atom's `source`, so binding one there renders nothing and reports
   * "asset not found".
   */
  mimeType: string | null;
  markets: string[];
  /** Key to values. One key can hold several, so a shot with two products says so. */
  tags: Record<string, string[]>;
  literalDescription: string | null;
  /** How ingestion renders it: lighting, palette, finish. Distinct from `literalDescription`, which says what is in frame. */
  styleDescription?: string | null;
  /** ImageMeta-shaped. Carries the focal the library recorded for this photograph. */
  engineMeta: Record<string, unknown>;
  /**
   * What the picture is of and where each one sits. The names are also in `tags.object`, which is
   * the half a filter matches on.
   *
   * Null means nothing has described this row yet; an empty list means it was described and nothing
   * in the picture is in focus. Optional on top of that because a deployment built before the
   * column existed omits the field entirely, which is a third thing again.
   */
  objects?: DetectedObject[] | null;
  /** Measured from the pixels. Optional because older rows were ingested before this was captured. */
  width?: number | null;
  /** Measured from the pixels. Optional because older rows were ingested before this was captured. */
  height?: number | null;
  /** A closed vocabulary ingestion chose from. */
  scene: string | null;
  /** Framing and distance, as parts joined by a middle dot. */
  shot: string | null;
  /** Measured from the pixels, not judged. */
  orientation: string | null;
  /** Empty means no licence tier was recorded. */
  licenseTiers: string[];
  /**
   * Where the asset is filed, null when it is in no folder.
   *
   * Optional because a deployment answering from before this field existed omits it, and a missing
   * folder must not read as "no folder" -- absent means unknown, which is a different thing to show.
   */
  folderId?: string | null;
  /** Past this the asset must not reach a piece. JSON carries it as a string, not a `Date`. */
  licenseExpiry: string | null;
  /** JSON carries it as a string, not a `Date`. */
  createdAt: string;
}

/**
 * Throwing rather than defaulting to an empty list.
 *
 * A failed fetch that returns `[]` renders the same empty state as a library with nothing eligible,
 * so someone is told the brand has no photography for this frame when in fact none could be loaded.
 */
export const listBrandMedia = async (query: ListBrandMediaQuery): Promise<BrandMediaItem[]> => {
  const response = await BrandMediaService.list({
    client: guidelinesHttp,
    query,
  });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The brand assets could not be loaded.'));
  }

  // The cast stays for the rest of the row, for the reason the header note gives. `objects` is
  // parsed out of it because it is the one field that reaches arithmetic and layout.
  const rows = response.data.data as unknown as BrandMediaItem[];
  return rows.map((row) => ({ ...row, objects: detectedObjectsFrom(row.objects) }));
};

/** What the library needs to record an asset that is already in assets-api. */
export interface CreateBrandMediaInput {
  assetId: string;
  filename: string;
  title: string;
  /**
   * What the browser said the file was.
   *
   * Load bearing rather than metadata: the server reads it to decide whether a row is footage, and
   * a row filed as an image has its own asset drawn by an `img` element -- which renders nothing for
   * a clip.
   */
  mimeType?: string;
  mediaType?: 'image' | 'video';
  folderId?: string;
  /**
   * Key to values, stated rather than left to ingestion.
   *
   * How an upload made for an image layer satisfies that layer's own rule: the row is born carrying
   * the tags the rule selects on, so it appears in the gallery it was added from rather than after a
   * model call. Ingestion fills only the keys a row leaves empty, so what is stated here survives
   * being catalogued.
   */
  tags?: Record<string, string[]>;
}

/**
 * Registers an uploaded asset as a library row.
 *
 * The bytes go to assets-api first and are already stored by the time this runs, so a failure here
 * leaves an orphaned asset rather than losing the file. That is the better half to fail: the row can
 * be created again from the same asset, and the upsert on `assetId` means doing so does not
 * duplicate anything.
 *
 * The server fills in what it can read from the picture. Nothing is sent for placement, description
 * or focal: ingestion measures and catalogues the row behind this call, and a guess made here would
 * be a fact nobody checked.
 */
export const createBrandMedia = async (input: CreateBrandMediaInput): Promise<BrandMediaItem> => {
  const response = await BrandMediaService.create({
    client: guidelinesHttp,
    body: input,
  });

  if (!response.data) {
    throw new Error(
      describeApiFailure(response.error, 'The file could not be added to the library.'),
    );
  }

  return response.data as unknown as BrandMediaItem;
};

/**
 * Sets one or more facet keys on a picture. Only the keys passed here change; a key left out is
 * untouched, and a key sent with an empty array is deleted server-side. Keys the ingest model
 * owns are silently ignored by the server rather than rejected, since a re-run replaces them
 * anyway.
 */
export const setMediaTags = async (
  mediaId: string,
  tags: Record<string, string[]>,
): Promise<BrandMediaItem> => {
  const response = await BrandMediaService.setTags({
    client: guidelinesHttp,
    path: { mediaId },
    body: { tags },
  });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The tags could not be updated.'));
  }

  return response.data as unknown as BrandMediaItem;
};

/**
 * Asks the server to work through whatever ingestion has not finished with.
 *
 * Workspace-wide rather than per asset, because the endpoint is: it selects the rows missing a
 * measurement, a description or an embedding rather than taking an id. Idempotent, so retrying from
 * one asset that came back untagged also picks up every other one still waiting -- which is what
 * somebody who just imported a pack of them actually wants.
 *
 * Answers as soon as the work is queued, not when it finishes. What it returns is how many rows were
 * taken on; the tags themselves arrive on a later fetch of the listing.
 */
export const ingestBrandMedia = async (): Promise<{ started: number }> => {
  const response = await BrandMediaService.ingest({ client: guidelinesHttp });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'Tagging could not be started again.'));
  }

  return response.data as { started: number };
};

/**
 * Retires media from the library.
 *
 * Soft on the server: the row and the stored bytes stay and every read filters them out, which is
 * why the copy around this says the assets stop being offered rather than that the files are gone.
 * Adding the same file again brings the row back.
 *
 * No cast, unlike its neighbours here: this is the one brand-media route that declares a response
 * schema, so the generated client already knows the shape.
 */
/**
 * Renames one tag value everywhere in the workspace, merging where the new name already exists.
 *
 * Server-side rather than a request per asset, unlike `useApplyTag`: a value sits on rows this
 * client has never fetched, so there is no list here to fan out over.
 */
export const renameTagValue = async (
  key: string,
  from: string,
  to: string,
): Promise<{ updated: number }> => {
  const response = await BrandMediaService.renameTagValue({
    client: guidelinesHttp,
    body: { key, from, to },
  });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The tag could not be renamed.'));
  }

  return response.data;
};

/** Takes one tag value off every asset carrying it. Not reversible: see the confirmation in front of it. */
export const removeTagValue = async (key: string, value: string): Promise<{ updated: number }> => {
  const response = await BrandMediaService.removeTagValue({
    client: guidelinesHttp,
    body: { key, value },
  });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The tag could not be removed.'));
  }

  return response.data;
};

export const archiveBrandMedia = async (
  mediaIds: readonly string[],
): Promise<{ archived: number }> => {
  const response = await BrandMediaService.archive({
    client: guidelinesHttp,
    body: { mediaIds: [...mediaIds] },
  });

  if (!response.data) {
    throw new Error(describeApiFailure(response.error, 'The assets could not be deleted.'));
  }

  return response.data;
};
